import { createReducer } from 'src/helpers/reducer';
import actionTypes from './actionTypes';
import type { IEditRateEngineState, IRateEngineVersion, ParsedErrorMap } from './types';

const initState: IEditRateEngineState = {
  loadedVersion: null,
  isLoadingVersion: false,
  loadVersionError: null,
  isSubmitting: false,
  parsedErrors: {},
  savedVersion: null,
};

function onLoadRequest(state: IEditRateEngineState): IEditRateEngineState {
  return { ...state, isLoadingVersion: true, loadVersionError: null };
}

function onLoadSuccess(
  state: IEditRateEngineState,
  { payload }: { payload: { version: IRateEngineVersion } },
): IEditRateEngineState {
  return { ...state, loadedVersion: payload.version, isLoadingVersion: false, loadVersionError: null };
}

// Keep the version when the failed load was a re-read of it (the editor re-reads after a 403 to show the real
// status); nulling it there would wipe unsaved work, since a null version reads as "copy source dropped".
function onLoadError(
  state: IEditRateEngineState,
  { payload }: { payload: { error: string; requestedId: string | null } },
): IEditRateEngineState {
  const wasReReadOfLoaded = !!payload.requestedId && payload.requestedId === state.loadedVersion?.id;

  return {
    ...state,
    loadedVersion: wasReReadOfLoaded ? state.loadedVersion : null,
    isLoadingVersion: false,
    loadVersionError: payload.error,
  };
}

function onClearLoadedVersion(state: IEditRateEngineState): IEditRateEngineState {
  return { ...state, loadedVersion: null, isLoadingVersion: false, loadVersionError: null };
}

function onSubmitRequest(state: IEditRateEngineState): IEditRateEngineState {
  return { ...state, isSubmitting: true, parsedErrors: {} };
}

// The response carries server-assigned ids and rewritten attributes, so it fills both slots: the draft re-seeds
// from `savedVersion`, and `loadedVersion` follows so a later reload agrees with it.
function onSubmitSuccess(
  state: IEditRateEngineState,
  { payload }: { payload: { version: IRateEngineVersion } },
): IEditRateEngineState {
  return {
    ...state,
    isSubmitting: false,
    parsedErrors: {},
    loadedVersion: payload.version,
    savedVersion: payload.version,
  };
}

function onSubmitFailed(
  state: IEditRateEngineState,
  { payload }: { payload: { parsedErrors: ParsedErrorMap } },
): IEditRateEngineState {
  return {
    ...state,
    isSubmitting: false,
    parsedErrors: payload.parsedErrors,
  };
}

function onSetParsedErrors(
  state: IEditRateEngineState,
  { payload }: { payload: { parsedErrors: ParsedErrorMap } },
): IEditRateEngineState {
  return { ...state, parsedErrors: payload.parsedErrors };
}

function onClearSubmitErrors(state: IEditRateEngineState): IEditRateEngineState {
  return { ...state, parsedErrors: {} };
}

export default createReducer(initState, {
  [actionTypes.LOAD_VERSION_REQUEST]: onLoadRequest,
  [actionTypes.LOAD_VERSION_SUCCESS]: onLoadSuccess,
  [actionTypes.LOAD_VERSION_ERROR]: onLoadError,
  [actionTypes.CLEAR_LOADED_VERSION]: onClearLoadedVersion,
  [actionTypes.SUBMIT_REQUEST]: onSubmitRequest,
  [actionTypes.SUBMIT_SUCCESS]: onSubmitSuccess,
  [actionTypes.SUBMIT_FAILED]: onSubmitFailed,
  [actionTypes.SET_PARSED_ERRORS]: onSetParsedErrors,
  [actionTypes.CLEAR_SUBMIT_ERRORS]: onClearSubmitErrors,
  [actionTypes.CLEAR_STORE]: () => initState,
});
